/**
 * AxLabs Experiments - Projectile Motion
 *
 * Classic kinematics experiment with optional air resistance.
 * Demonstrates parabolic trajectories and the effect of drag.
 *
 * Governing equations:
 * Without drag: x(t) = v₀cos(θ)t, y(t) = v₀sin(θ)t - ½gt²
 * With drag: F_drag = -½ρCdAv²
 */
import { BaseSimulationEngine, DIMENSIONS, vec2, scale2, magnitude2, normalize2, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'initialVelocity',
        name: 'Initial Velocity',
        symbol: 'v_0',
        unit: 'm/s',
        dimension: DIMENSIONS.VELOCITY,
        min: 1,
        max: 100,
        default: 20,
        step: 1,
        description: 'Launch speed of the projectile',
    },
    {
        id: 'launchAngle',
        name: 'Launch Angle',
        symbol: '\\theta',
        unit: '°',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 90,
        default: 45,
        step: 1,
        description: 'Angle above horizontal at launch',
    },
    {
        id: 'initialHeight',
        name: 'Initial Height',
        symbol: 'h_0',
        unit: 'm',
        dimension: DIMENSIONS.LENGTH,
        min: 0,
        max: 100,
        default: 0,
        step: 0.5,
        description: 'Height above ground at launch',
    },
    {
        id: 'gravity',
        name: 'Gravitational Acceleration',
        symbol: 'g',
        unit: 'm/s²',
        dimension: DIMENSIONS.ACCELERATION,
        min: 0.1,
        max: 25,
        default: 9.81,
        step: 0.01,
        description: 'Local gravitational field strength',
    },
    {
        id: 'mass',
        name: 'Projectile Mass',
        symbol: 'm',
        unit: 'kg',
        dimension: DIMENSIONS.MASS,
        min: 0.001,
        max: 100,
        default: 1,
        step: 0.1,
        description: 'Mass of the projectile',
    },
    {
        id: 'dragCoefficient',
        name: 'Drag Coefficient',
        symbol: 'C_d',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 0,
        max: 2,
        default: 0,
        step: 0.01,
        description: 'Aerodynamic drag coefficient (0 = no air resistance)',
    },
    {
        id: 'crossSectionArea',
        name: 'Cross-Section Area',
        symbol: 'A',
        unit: 'm²',
        dimension: { L: 2, M: 0, T: 0, I: 0, Θ: 0, N: 0, J: 0 },
        min: 0.0001,
        max: 1,
        default: 0.01,
        step: 0.001,
        description: 'Frontal cross-sectional area of projectile',
    },
    {
        id: 'airDensity',
        name: 'Air Density',
        symbol: '\\rho',
        unit: 'kg/m³',
        dimension: { L: -3, M: 1, T: 0, I: 0, Θ: 0, N: 0, J: 0 },
        min: 0,
        max: 2,
        default: 1.225,
        step: 0.001,
        description: 'Density of air (1.225 kg/m³ at sea level)',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'position_x',
        name: 'Horizontal Position (no drag)',
        latex: 'x(t) = v_0 \\cos(\\theta) \\cdot t',
        variables: ['x', 'initialVelocity', 'launchAngle'],
        description: 'Horizontal position as function of time (no air resistance)',
        condition: (params) => (params['dragCoefficient'] ?? 0) === 0,
    },
    {
        id: 'position_y',
        name: 'Vertical Position (no drag)',
        latex: 'y(t) = h_0 + v_0 \\sin(\\theta) \\cdot t - \\frac{1}{2}gt^2',
        variables: ['y', 'initialHeight', 'initialVelocity', 'launchAngle', 'gravity'],
        description: 'Vertical position as function of time (no air resistance)',
        condition: (params) => (params['dragCoefficient'] ?? 0) === 0,
    },
    {
        id: 'range',
        name: 'Range (no drag)',
        latex: 'R = \\frac{v_0^2 \\sin(2\\theta)}{g}',
        variables: ['initialVelocity', 'launchAngle', 'gravity'],
        description: 'Maximum horizontal distance (from ground level)',
        condition: (params) => (params['dragCoefficient'] ?? 0) === 0 && (params['initialHeight'] ?? 0) === 0,
    },
    {
        id: 'max_height',
        name: 'Maximum Height',
        latex: 'h_{max} = h_0 + \\frac{(v_0 \\sin\\theta)^2}{2g}',
        variables: ['initialHeight', 'initialVelocity', 'launchAngle', 'gravity'],
        description: 'Peak height of trajectory',
    },
    {
        id: 'drag_force',
        name: 'Drag Force',
        latex: 'F_d = \\frac{1}{2} \\rho C_d A v^2',
        variables: ['airDensity', 'dragCoefficient', 'crossSectionArea', 'vx', 'vy'],
        description: 'Air resistance force (opposes velocity)',
        condition: (params) => (params['dragCoefficient'] ?? 0) > 0,
    },
    {
        id: 'kinetic_energy',
        name: 'Kinetic Energy',
        latex: 'KE = \\frac{1}{2}m(v_x^2 + v_y^2)',
        variables: ['mass', 'vx', 'vy'],
        description: 'Energy due to motion',
    },
    {
        id: 'potential_energy',
        name: 'Potential Energy',
        latex: 'PE = mgy',
        variables: ['mass', 'gravity', 'y'],
        description: 'Gravitational potential energy',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'below_ground',
        name: 'Below Ground',
        description: 'Projectile has gone below ground level',
        check: (state) => (state.variables['y'] ?? 0) < -0.01,
        severity: 'warning',
        educationalMessage: 'The projectile has landed. In reality, it would stop upon impact with the ground.',
    },
    {
        id: 'terminal_velocity',
        name: 'Terminal Velocity Reached',
        description: 'Drag force equals gravitational force',
        check: (state, params) => {
            const vy = state.variables['vy'] ?? 0;
            const g = params['gravity'] ?? 9.81;
            const m = params['mass'] ?? 1;
            const rho = params['airDensity'] ?? 1.225;
            const Cd = params['dragCoefficient'] ?? 0;
            const A = params['crossSectionArea'] ?? 0.01;
            if (Cd === 0)
                return false;
            // Terminal velocity: v_t = sqrt(2mg / (ρCdA))
            const vTerminal = Math.sqrt(2 * m * g / (rho * Cd * A));
            return Math.abs(vy) > 0.95 * vTerminal && vy < 0;
        },
        severity: 'warning',
        educationalMessage: 'The projectile has reached terminal velocity. Drag force now equals weight, ' +
            'so acceleration is zero and velocity remains constant.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const PROJECTILE_MOTION_CONFIG = {
    id: 'projectile-motion',
    slug: 'projectile-motion',
    name: 'Projectile Motion',
    domain: 'physics',
    description: 'Analyze projectile trajectories with optional air resistance',
    longDescription: `
Projectile motion demonstrates the independence of horizontal and vertical motion
under the influence of gravity (and optionally, air resistance).

Key concepts:
- Without drag, horizontal velocity remains constant
- Vertical motion is uniformly accelerated by gravity
- Trajectory is parabolic (without drag)
- Range is maximized at 45° (from ground level, no drag)
- With drag, trajectory is asymmetric and range decreases
- Terminal velocity occurs when drag equals weight

This simulation allows you to toggle air resistance to see how
real-world conditions differ from the idealized case.
  `.trim(),
    version: 1,
    conceptTags: ['kinematics', 'projectile', 'drag', 'energy'],
    difficulty: 2,
    prerequisites: ['vectors', 'kinematics'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'x', type: 'fixed', value: 0 },
        { variableId: 'y', type: 'parameter', parameterId: 'initialHeight' },
        { variableId: 'vx', type: 'computed', compute: (p) => {
                const v0 = p['initialVelocity'] ?? 20;
                const angle = (p['launchAngle'] ?? 45) * Math.PI / 180;
                return v0 * Math.cos(angle);
            } },
        { variableId: 'vy', type: 'computed', compute: (p) => {
                const v0 = p['initialVelocity'] ?? 20;
                const angle = (p['launchAngle'] ?? 45) * Math.PI / 180;
                return v0 * Math.sin(angle);
            } },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 1 / 120,
    maxDt: 1 / 30,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class ProjectileMotionSimulation extends BaseSimulationEngine {
    config = PROJECTILE_MOTION_CONFIG;
    constructor() {
        super({ x: 0, y: 0, vx: 0, vy: 0, distance: 0, landed: 0 }, { type: 'euler', dt: 1 / 120, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const v0 = params['initialVelocity'] ?? 20;
        const angleDeg = params['launchAngle'] ?? 45;
        const angleRad = angleDeg * (Math.PI / 180);
        const h0 = params['initialHeight'] ?? 0;
        return {
            x: 0,
            y: h0,
            vx: v0 * Math.cos(angleRad),
            vy: v0 * Math.sin(angleRad),
            distance: 0,
            landed: 0,
        };
    }
    computeDerivatives(state, params) {
        // If landed, no more motion
        if (state.landed === 1) {
            return { x: 0, y: 0, vx: 0, vy: 0 };
        }
        const g = params['gravity'] ?? 9.81;
        const m = params['mass'] ?? 1;
        const Cd = params['dragCoefficient'] ?? 0;
        const A = params['crossSectionArea'] ?? 0.01;
        const rho = params['airDensity'] ?? 1.225;
        // Gravity acceleration
        let ax = 0;
        let ay = -g;
        // Add drag if enabled
        if (Cd > 0) {
            const velocity = vec2(state.vx, state.vy);
            const speed = magnitude2(velocity);
            if (speed > 0) {
                // Drag force: F_d = (1/2) * ρ * Cd * A * v²
                const dragMagnitude = 0.5 * rho * Cd * A * speed * speed;
                // Drag acceleration (opposite to velocity direction)
                const dragDir = normalize2(velocity);
                const dragAcc = scale2(dragDir, -dragMagnitude / m);
                ax += dragAcc.x;
                ay += dragAcc.y;
            }
        }
        return {
            x: state.vx,
            y: state.vy,
            vx: ax,
            vy: ay,
        };
    }
    integrate(dt) {
        // Skip if already landed
        if (this.state.landed === 1) {
            return;
        }
        const derivs = this.derivatives;
        const prevX = this.state.x;
        const prevY = this.state.y;
        // Semi-implicit Euler
        this.state.vx += (derivs.vx ?? 0) * dt;
        this.state.vy += (derivs.vy ?? 0) * dt;
        this.state.x += this.state.vx * dt;
        this.state.y += this.state.vy * dt;
        // Update distance traveled
        const dx = this.state.x - prevX;
        const dy = this.state.y - prevY;
        this.state.distance += Math.sqrt(dx * dx + dy * dy);
        // Check for landing
        if (this.state.y <= 0 && this.state.vy < 0) {
            // Interpolate to ground level
            const t = -prevY / (this.state.y - prevY);
            this.state.x = prevX + t * dx;
            this.state.y = 0;
            this.state.vx = 0;
            this.state.vy = 0;
            this.state.landed = 1;
        }
    }
    computeEnergy(state, params) {
        const m = params['mass'] ?? 1;
        const g = params['gravity'] ?? 9.81;
        const speed2 = state.vx * state.vx + state.vy * state.vy;
        const kinetic = 0.5 * m * speed2;
        const potential = m * g * state.y;
        return {
            kinetic,
            potential,
            thermal: 0,
            total: kinetic + potential,
        };
    }
    getPositionForStabilityCheck() {
        return this.state.y;
    }
    /**
     * Calculate theoretical range (no drag, from ground level)
     */
    getTheoreticalRange() {
        const v0 = this.parameters['initialVelocity'] ?? 20;
        const angleDeg = this.parameters['launchAngle'] ?? 45;
        const angleRad = angleDeg * (Math.PI / 180);
        const g = this.parameters['gravity'] ?? 9.81;
        const h0 = this.parameters['initialHeight'] ?? 0;
        if (h0 === 0) {
            // Simple case: R = v0² sin(2θ) / g
            return (v0 * v0 * Math.sin(2 * angleRad)) / g;
        }
        // With initial height
        const vx = v0 * Math.cos(angleRad);
        const vy = v0 * Math.sin(angleRad);
        // Time to hit ground: h0 + vy*t - 0.5*g*t² = 0
        const discriminant = vy * vy + 2 * g * h0;
        if (discriminant < 0)
            return 0;
        const t = (vy + Math.sqrt(discriminant)) / g;
        return vx * t;
    }
    /**
     * Calculate theoretical maximum height
     */
    getTheoreticalMaxHeight() {
        const v0 = this.parameters['initialVelocity'] ?? 20;
        const angleDeg = this.parameters['launchAngle'] ?? 45;
        const angleRad = angleDeg * (Math.PI / 180);
        const g = this.parameters['gravity'] ?? 9.81;
        const h0 = this.parameters['initialHeight'] ?? 0;
        const vy = v0 * Math.sin(angleRad);
        return h0 + (vy * vy) / (2 * g);
    }
}
/**
 * Factory function to create a new projectile motion simulation
 */
export function createProjectileMotion() {
    return new ProjectileMotionSimulation();
}
//# sourceMappingURL=projectile-motion.js.map